import { Check, X } from "lucide-react";
import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import type { EnglishComparePage } from "@/app/seo-compare-en";

export function CompareLayoutEn({ page }: { page: EnglishComparePage }) {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <ContentHero
        eyebrow={page.eyebrow}
        eyebrowColor="#A3E635"
        titleTop="Hotline vs"
        titleBottom={page.otherName}
        bottomColor={page.otherColor}
        lead={page.tldr}
        meta={["English compare", `${page.dimensions.length} dimensions`, `${page.faqs.length} FAQ items`]}
        breadcrumbs={[
          { label: "English Home", href: "/en/" },
          { label: "Compare" },
          { label: page.otherName },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <section className="border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-black">KEY TAKEAWAYS</p>
          <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">In three lines</h2>
          <ul className="mt-5 grid gap-3 text-sm font-bold leading-7 text-black sm:text-base">
            {page.takeaways.map((takeaway) => (
              <li key={takeaway} className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center border-2 border-black bg-black">
                  <Check className="h-3 w-3 text-[#A3E635]" strokeWidth={4} />
                </span>
                <span>{takeaway}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <header>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">DIMENSIONS</p>
            <h2 className="mt-2 text-2xl font-black uppercase text-black sm:text-3xl">Dimension by dimension</h2>
            <p className="mt-3 max-w-3xl text-sm font-bold leading-6 text-zinc-700 sm:text-base">
              Each row compares what Hotline standardizes, what {page.otherName} focuses on, and why the difference matters.
            </p>
          </header>

          <div className="mt-6 overflow-hidden border-4 border-black bg-white/85 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            <div className="grid grid-cols-1 border-b-4 border-black bg-black text-white sm:grid-cols-[1.2fr_1.6fr_1.6fr]">
              <div className="p-4 text-xs font-black uppercase tracking-[0.22em] text-zinc-300">Dimension</div>
              <div className="border-t-4 border-black p-4 text-xs font-black uppercase tracking-[0.22em] text-[#A3E635] sm:border-l-4 sm:border-t-0">
                Hotline
              </div>
              <div
                className="border-t-4 border-black p-4 text-xs font-black uppercase tracking-[0.22em] sm:border-l-4 sm:border-t-0"
                style={{ color: page.otherColor }}
              >
                {page.otherName}
              </div>
            </div>

            {page.dimensions.map((dimension, index) => (
              <div
                key={dimension.label}
                className={`grid grid-cols-1 sm:grid-cols-[1.2fr_1.6fr_1.6fr] ${index !== page.dimensions.length - 1 ? "border-b-4 border-black" : ""}`}
              >
                <div className="bg-[#F8F6EF] p-4 text-sm font-black uppercase tracking-[0.12em] text-black">{dimension.label}</div>
                <div className="border-t-2 border-black bg-[#A3E635]/30 p-4 text-sm font-bold leading-6 text-zinc-900 sm:border-l-4 sm:border-t-0">
                  <p>{dimension.hotline}</p>
                  <p className="mt-3 text-[11px] font-black uppercase tracking-[0.18em] text-zinc-600">Why it matters</p>
                  <p className="mt-1 text-xs font-bold leading-5 text-zinc-700">{dimension.why}</p>
                </div>
                <div className="border-t-2 border-black bg-white p-4 text-sm font-bold leading-6 text-zinc-700 sm:border-l-4 sm:border-t-0">
                  {dimension.other}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid gap-5 lg:grid-cols-2">
          <article className="border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-black">WHEN HOTLINE</p>
            <h3 className="mt-3 text-xl font-black uppercase text-black sm:text-2xl">Choose Hotline when...</h3>
            <p className="mt-4 text-sm font-bold leading-7 text-black sm:text-base">{page.whenHotline}</p>
            <div className="mt-5 inline-flex items-center gap-2 border-2 border-black bg-black px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635]">
              <Check className="h-3.5 w-3.5" strokeWidth={4} />
              Use Hotline
            </div>
          </article>

          <article className="border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: page.otherColor }}>
              WHEN {page.otherName.toUpperCase()}
            </p>
            <h3 className="mt-3 text-xl font-black uppercase text-black sm:text-2xl">Choose {page.otherName} when...</h3>
            <p className="mt-4 text-sm font-bold leading-7 text-zinc-700 sm:text-base">{page.whenOther}</p>
            <div className="mt-5 inline-flex items-center gap-2 border-2 border-black bg-zinc-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-black">
              <X className="h-3.5 w-3.5" strokeWidth={4} />
              Not necessarily Hotline
            </div>
          </article>
        </section>

        <section className="mt-12 border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">PAGE FAQ</p>
          <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">
            Common questions about {page.otherName}
          </h2>
          <div className="mt-6 grid gap-4">
            {page.faqs.map((faq) => (
              <article key={faq.question} className="border-4 border-black bg-[#F8F6EF] p-5">
                <h3 className="text-base font-black text-black sm:text-lg">{faq.question}</h3>
                <p className="mt-3 text-sm font-bold leading-7 text-zinc-700">{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <aside className="mt-14 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">FROM HERE</p>
          <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">Take the next step</h2>
          <p className="mt-3 text-sm font-bold leading-6 text-zinc-300 sm:text-base">
            The comparison is only useful if it helps you choose an implementation path. Start with the docs overview or jump into the current Marketplace surface.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {page.nextLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`inline-flex items-center gap-2 border-4 border-black px-5 py-3 text-sm font-black uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                  link.tone === "primary" ? "bg-[#A3E635] text-black" : "bg-white text-black"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
